import { useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { RocketLaunchIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

export default function HandoverSection({ gym, onUpdate }) {
  const { user } = useAuth();
  const isCEO = user?.role?.level === 1;
  const isHandoverStage = ['Go-Live Ready', 'Go-Live Done'].includes(gym.stage);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ salesPerson: '', day0LaunchDate: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  if (!isHandoverStage) return null;

  const handleHandover = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`/api/gyms/${gym.id}/handover`, form);
      toast.success('Gym handed over to Sales — Day 0 Launch scheduled!');
      setShowForm(false);
      onUpdate?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record handover');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 mt-4">
      <div className="flex items-center gap-2 mb-4">
        <RocketLaunchIcon className="h-5 w-5 text-red-600" />
        <h3 className="text-base font-semibold text-gray-800">Gym Handover — Day 0 Launch</h3>
      </div>

      {gym.stage === 'Go-Live Done' && gym.handoverToSalesAt ? (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            <span className="text-sm font-semibold text-green-800">Handed over to Sales</span>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Sales Person</p>
              <p className="font-medium text-gray-800">{gym.handoverSalesPerson || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Handover Date</p>
              <p className="font-medium text-gray-800">
                {new Date(gym.handoverToSalesAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Day 0 Launch Date</p>
              <p className="font-bold text-red-700">
                {gym.day0LaunchDate
                  ? new Date(gym.day0LaunchDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                  : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Work Completed</p>
              <p className="font-medium text-gray-800">
                {gym.workCompletedAt
                  ? new Date(gym.workCompletedAt).toLocaleDateString('en-IN')
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      ) : isCEO ? (
        <>
          <p className="text-sm text-gray-500 mb-4">
            Confirm that all facility work is complete and this gym is ready for Day 0 launch.
          </p>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-5 py-2 rounded-lg"
            >
              Record Handover to Sales
            </button>
          ) : (
            <form onSubmit={handleHandover} className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sales Person Name <span className="text-red-500">*</span></label>
                <input
                  type="text" required
                  value={form.salesPerson}
                  onChange={e => setForm(p => ({ ...p, salesPerson: e.target.value }))}
                  placeholder="e.g. Ravi Kumar"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Day 0 Launch Date <span className="text-red-500">*</span></label>
                <input
                  type="date" required
                  value={form.day0LaunchDate}
                  onChange={e => setForm(p => ({ ...p, day0LaunchDate: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes (optional)</label>
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Any final notes for Sales team..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-red-500"
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={submitting}
                  className="bg-red-600 text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50">
                  {submitting ? 'Processing...' : 'Confirm Handover & Go-Live Done'}
                </button>
                <button type="button" onClick={() => setShowForm(false)}
                  className="border border-gray-300 text-sm px-4 py-2 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </form>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500 italic">Awaiting CEO approval for handover to Sales.</p>
      )}
    </div>
  );
}
